<!-- ResetPassword.vue -->
<template>
  <auth-form
    :val-error="valError"
    :error-message="errorMessage"
    :show-logging-in="showLoggingIn"
    :logging-in="loggingIn"
    action-text=""
    link-text=""
    link-label=""
    link-to=""
    :show-social-logins="false"
    :show-button="!successMessage"
    @submit="handleSubmit"
  >
    <template #title>
      <h2 v-if="!successMessage">Reset password</h2>
      <p v-if="!successMessage">
        Enter your email address below, and we'll send you a link to set a new password
      </p>
    </template>
    <template #form-fields>
      <template v-if="successMessage">
        <div class="success-container">
          <h2 class="check-email-title">Check your email</h2>
          <p class="instructions">
            We sent a password reset link to <strong>{{ maskedEmail }}</strong>.
            Open your email app and make sure you're signed in with this address.
          </p>

          <router-link :to="{ name: 'login' }" class="secondary-action">
            Back to login
          </router-link>

          <div class="resend-section">
            <p class="resend-question">Didn't receive the email?</p>
            <p class="resend-hint">
              Check your spam folder or
              <template v-if="resendCountdown > 0">
                resend the link in {{ resendCountdown }}
                second<template v-if="resendCountdown !== 1">s</template>
              </template>
              <a v-else href="#" @click.prevent="resendLink">resend the link</a>
            </p>
            <a href="#" class="different-email" @click.prevent="useDifferentEmail">
              Use a different email
            </a>
          </div>
        </div>
      </template>
      <template v-else>
        <label for="email">Email *</label>
        <input
          type="email"
          id="email"
          v-model="email"
          placeholder="Enter your email"
          :class="{ 'is-invalid': submitted && !email }"
        />
        <div v-show="submitted && !email" class="invalid-feedback">
          Email is required
        </div>
        <div class="back-to-login">
          <router-link :to="{ name: 'login' }">
            <span>Back to login</span>
          </router-link>
        </div>
      </template>
    </template>
    <template v-if="!successMessage" #button-text>
      Send link
    </template>
  </auth-form>
</template>

<script>
import AuthForm from "./AuthForm.vue";
import { mapState } from "vuex";
import { apiService } from "../../_services";

const RESET_FAILED_MESSAGE = "Failed to process password reset request, Try again.";
const RESEND_COOLDOWN = 45;

export default {
  components: { AuthForm },
  data() {
    return {
      email: "",
      submitted: false,
      valError: false,
      errorMessage: "",
      showLoggingIn: false,
      successMessage: false,
      resendCountdown: 0,
      resendTimer: null
    };
  },
  computed: {
    ...mapState("authentication", ["loggingIn"]),
    maskedEmail() {
      const [local, domain] = this.email.split("@");
      if (!domain) return this.email;
      const visible = local.slice(0, 2);
      return `${visible}••••••@${domain}`;
    }
  },
  created() {
    document.title = "Quanspire | Reset Password";
    this.$store.dispatch("authentication/logout");
  },
  beforeDestroy() {
    this.clearResendTimer();
  },
  methods: {
    async handleSubmit() {
      this.submitted = true;

      if (!this.email) {
        this.valError = true;
        this.errorMessage = "Email is required";
        this.showLoggingIn = false;
        return;
      }

      const sent = await this.sendResetEmail();
      if (sent) {
        this.successMessage = true;
        this.startResendCountdown();
      }
    },
    async sendResetEmail() {
      this.valError = false;
      this.errorMessage = "";
      this.showLoggingIn = true;
      try {
        const data = await apiService.resetPassword(this.email);
        let message = "";
        Object.keys(data).forEach((key) => {
          message = data[key];
        });

        this.showLoggingIn = false;

        if (message === RESET_FAILED_MESSAGE) {
          this.valError = true;
          this.errorMessage = message;
          return false;
        }
        return true;
      } catch (err) {
        this.valError = true;
        this.errorMessage = err.message || "An error occurred. Please try again.";
        this.showLoggingIn = false;
        return false;
      }
    },
    async resendLink() {
      if (this.resendCountdown > 0) return;
      const sent = await this.sendResetEmail();
      if (sent) {
        this.startResendCountdown();
      }
    },
    useDifferentEmail() {
      this.successMessage = false;
      this.submitted = false;
      this.valError = false;
      this.errorMessage = "";
      this.clearResendTimer();
      this.resendCountdown = 0;
    },
    startResendCountdown() {
      this.resendCountdown = RESEND_COOLDOWN;
      this.clearResendTimer();
      this.resendTimer = setInterval(() => {
        this.resendCountdown -= 1;
        if (this.resendCountdown <= 0) {
          this.clearResendTimer();
        }
      }, 1000);
    },
    clearResendTimer() {
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
    }
  }
};
</script>

<style scoped>
.is-invalid {
  border-color: var(--red);
}

.invalid-feedback {
  color: var(--red);
  font-size: 12px;
  margin-top: 5px;
}

.back-to-login {
  margin-top: 10px;
  font-size: 12px;
}

.back-to-login a {
  color: var(--expo);
  text-decoration: none;
  font-weight: bold;
  transition: color 0.3s ease;
}

.back-to-login a:hover {
  color: var(--dark-expo);
}

.success-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 10px 0;
}

.check-email-title {
  font-size: 28px;
  font-weight: normal;
  color: var(--textInactiveFixed);
  margin: 0 0 12px;
}

.instructions {
  color: var(--textInactiveFixed);
  font-size: 14px;
  line-height: 1.4;
  margin: 0 0 20px;
}

.instructions strong {
  color: var(--textactive-2);
}

.secondary-action {
  margin-top: 14px;
  color: var(--expo);
  text-decoration: none;
  font-weight: bold;
  font-size: 14px;
  transition: color 0.3s ease;
}

.secondary-action:hover {
  color: var(--dark-expo);
}

.resend-section {
  width: 100%;
  margin-top: 26px;
  padding-top: 18px;
  border-top: 1px solid var(--textInactiveFixed);
}

.resend-question {
  color: var(--textactive-2);
  font-size: 13px;
  margin: 0 0 4px;
}

.resend-hint {
  color: var(--textInactiveFixed);
  font-size: 13px;
  margin: 0 0 12px;
  line-height: 1.4;
}

.resend-hint a,
.different-email {
  color: var(--expo);
  text-decoration: none;
  font-weight: bold;
  cursor: pointer;
  transition: color 0.3s ease;
}

.resend-hint a:hover,
.different-email:hover {
  color: var(--dark-expo);
}

.different-email {
  font-size: 13px;
}
</style>
